import { useState } from "react";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import { SendLinkModal } from "../components/SendLinkModal";
import { TOTAL_JOURNEY_MINUTES } from "../components/ProgressBar";

interface WelcomeScreenProps {
  lang: Lang;
  onLogin: () => void;
  loading: boolean;
  error: string | null;
}

/** Step 1 — Sign in with Microsoft 365. */
export function WelcomeScreen({ lang, onLogin, loading, error }: WelcomeScreenProps) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [showSendLink, setShowSendLink] = useState(false);

  return (
    <div className="kd-step-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="glass-panel step-login">
        <div className="kd-step-header">
          <h1>{t("أهلاً بك في كادن", "Welcome to Kaden")}</h1>
          <p>
            {t(
              "يسرّنا انضمامك إلى فريق كادن للاستثمار. سترشدك هذه الرحلة القصيرة إلى أنظمتك وفريقك وكل ما تحتاج معرفته في يومك الأول. سجّل دخولك بحساب Microsoft 365 الخاص بالشركة للبدء.",
              "We are pleased to welcome you to the Kaden Investment team. This short journey will guide you through your systems, your team, and everything you need to know on your first day. Sign in with your company Microsoft 365 account to begin.",
            )}
          </p>
        </div>

        <div className="kd-guide-popup-chips" style={{ justifyContent: "center", margin: "0 0 1.25rem" }}>
          <span className="kd-guide-chip">
            ⏱ {t(`حوالي ${TOTAL_JOURNEY_MINUTES} دقيقة فقط`, `About ${TOTAL_JOURNEY_MINUTES} minutes total`)}
          </span>
        </div>

        <div className="kd-login-card">
          <div className="kd-login-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <rect x="1" y="1" width="10" height="10" fill="#f25022" rx="1" />
              <rect x="13" y="1" width="10" height="10" fill="#7fba00" rx="1" />
              <rect x="1" y="13" width="10" height="10" fill="#00a4ef" rx="1" />
              <rect x="13" y="13" width="10" height="10" fill="#ffb900" rx="1" />
            </svg>
          </div>

          <p className="kd-login-desc">
            {t(
              "ستظهر لك نافذة تسجيل دخول Microsoft, استخدم البريد الإلكتروني وكلمة المرور التي استلمتها من قسم تقنية المعلومات.",
              "A Microsoft sign-in window will appear, use the email address and password you received from the IT department.",
            )}
          </p>

          <button className="kd-btn-primary kd-btn-gold" onClick={onLogin} disabled={loading}>
            {loading ? (
              <span className="kd-spinner" />
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                  <polyline points="10 17 15 12 10 7" />
                  <line x1="15" y1="12" x2="3" y2="12" />
                </svg>
                <span>{t("تسجيل الدخول بحسابك", "Sign in with your account")}</span>
              </>
            )}
          </button>

          {error && <p className="kd-error">{error}</p>}

          <p className="kd-mobile-hint">
            {t(
              "لأفضل تجربة ممكنة ننصح باستخدام جهاز اللاب توب لإكمال الرحلة.",
              "For the best experience, we recommend completing the journey on a laptop.",
            )}
          </p>

          <button type="button" className="kd-send-link-btn" onClick={() => setShowSendLink(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
            <span>{t("تتصفح من الجوال؟ أرسل لي الرابط لاحقاً", "On mobile? Send me the link later")}</span>
          </button>
        </div>

        <div className="kd-inline-note kd-inline-note-centered">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span>
            {t(
              "إذا لم تستلم بيانات حسابك بعد، يُرجى التواصل مع فريق تقنية المعلومات عبر زر (?) أسفل الشاشة قبل المتابعة.",
              "If you have not yet received your account credentials, please contact the IT team using the (?) button at the bottom of the screen before proceeding.",
            )}
          </span>
        </div>
      </div>

      {showSendLink && <SendLinkModal lang={lang} onClose={() => setShowSendLink(false)} />}
    </div>
  );
}
